import { USER_ROLE_LABELS, USER_STATUS_LABELS, type UserSummary } from '@acasa/shared'
import { useState } from 'react'
import { Card } from '@/components/card'
import { TextInput } from '@/features/handovers/fields'
import { ApiRequestError } from '@/lib/api'
import {
  useCreateUser,
  useDeleteUser,
  useIssuePasswordReset,
  useReinvite,
  useSession,
  useUpdateUser,
  useUsers,
} from './use-auth'

/**
 * Account administration. Admin only, both here and — the half that counts — in
 * the API.
 *
 * Creating an account, re-inviting one or resetting a password each produces a
 * LINK. Until the server can send mail, the admin copies it from here and hands
 * it over however the agency already communicates. It is shown exactly once:
 * only its hash is stored.
 *
 * The two per-row links mean different things and are labelled so. "Passwort
 * zurücksetzen" replaces the password and leaves the authenticator alone;
 * "neu einrichten" is the full enrolment again — the lost-phone path.
 */
type IssuedLink = { kind: 'invite' | 'reset'; email: string; url: string }

const LINK_LABELS: Record<IssuedLink['kind'], string> = {
  invite: 'Einladungslink',
  reset: 'Link zum Zurücksetzen des Passworts',
}

export function UserAdminCard() {
  const { user } = useSession()
  const isAdmin = user?.role === 'admin'
  const users = useUsers(isAdmin)

  const create = useCreateUser()
  const reinvite = useReinvite()
  const issueReset = useIssuePasswordReset()
  const update = useUpdateUser()
  const remove = useDeleteUser()

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<'admin' | 'agent'>('agent')
  const [link, setLink] = useState<IssuedLink | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!isAdmin) return null

  const rows = users.data?.users ?? []

  const handleError = (mutationError: unknown) =>
    setError(mutationError instanceof ApiRequestError ? mutationError.message : 'Fehler')

  return (
    <Card
      title="Konten"
      footer="Konten werden ausschließlich hier angelegt — es gibt keine Selbstregistrierung. Einladungs- und Zurücksetzungslinks gehen per E-Mail hinaus, sobald der Server Mails versenden kann — bis dahin kopiert ein Administrator sie hier, wo sie nur einmal angezeigt werden."
    >
      <form
        className="mb-6 flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          create.mutate(
            { email: email.trim(), name: name.trim(), role },
            {
              onSuccess: (response) => {
                setLink({ kind: 'invite', email: response.user.email, url: response.enrolmentUrl })
                setEmail('')
                setName('')
                setRole('agent')
              },
              onError: handleError,
            },
          )
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            type="email"
            placeholder="E-Mail"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
          <TextInput
            placeholder="Vor- und Nachname"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="text-muted flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={role === 'admin'}
              onChange={(event) => setRole(event.target.checked ? 'admin' : 'agent')}
              className="accent-brand"
            />
            Administrator (darf Konten verwalten)
          </label>

          <button
            type="submit"
            disabled={create.isPending}
            className="bg-brand text-surface hover:bg-brand-strong ml-auto px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
          >
            {create.isPending ? 'wird angelegt …' : 'Konto anlegen'}
          </button>
        </div>
      </form>

      {link && (
        <div className="border-line bg-raised mb-6 border p-3">
          <p className="text-muted text-xs">
            {LINK_LABELS[link.kind]} für <span className="text-ink">{link.email}</span> — jetzt
            kopieren, er wird nicht noch einmal angezeigt.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="text-ink min-w-0 flex-1 truncate font-mono text-[11px]">
              {link.url}
            </code>
            <button
              type="button"
              onClick={() => void navigator.clipboard?.writeText(link.url)}
              className="border-line hover:border-line-strong text-muted hover:text-ink shrink-0 border px-2 py-1 text-[11px] transition-colors"
            >
              kopieren
            </button>
          </div>
        </div>
      )}

      <ul className="flex flex-col">
        {rows.map((row) => (
          <UserRow
            key={row.id}
            row={row}
            isSelf={row.id === user?.id}
            busy={
              update.isPending || remove.isPending || reinvite.isPending || issueReset.isPending
            }
            onReinvite={() =>
              reinvite.mutate(row.id, {
                onSuccess: (response) =>
                  setLink({
                    kind: 'invite',
                    email: response.user.email,
                    url: response.enrolmentUrl,
                  }),
                onError: handleError,
              })
            }
            onResetPassword={() =>
              issueReset.mutate(row.id, {
                onSuccess: (response) =>
                  setLink({ kind: 'reset', email: response.user.email, url: response.resetUrl }),
                onError: handleError,
              })
            }
            onToggle={() =>
              update.mutate(
                { id: row.id, status: row.status === 'disabled' ? 'active' : 'disabled' },
                { onError: handleError },
              )
            }
            onDelete={() => remove.mutate(row.id, { onError: handleError })}
          />
        ))}

        {rows.length === 0 && !users.isLoading && (
          <li className="text-muted py-2 text-sm">Noch keine weiteren Konten.</li>
        )}
      </ul>

      {error && <p className="text-danger mt-3 text-xs">{error}</p>}
    </Card>
  )
}

function UserRow({
  row,
  isSelf,
  busy,
  onReinvite,
  onResetPassword,
  onToggle,
  onDelete,
}: {
  row: UserSummary
  isSelf: boolean
  busy: boolean
  onReinvite: () => void
  onResetPassword: () => void
  onToggle: () => void
  onDelete: () => void
}) {
  const active = row.status === 'active'

  return (
    <li className="border-line flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b py-2.5 last:border-0">
      <span className="min-w-0">
        <span className="text-ink block truncate text-sm">
          {row.name}
          {isSelf && <span className="text-faint"> — Sie</span>}
        </span>
        <span className="text-faint block truncate font-mono text-[11px]">{row.email}</span>
      </span>

      <span className="text-faint flex shrink-0 items-baseline gap-3 text-[11px]">
        <span>{USER_ROLE_LABELS[row.role]}</span>
        <span className={row.status === 'disabled' ? 'text-danger' : undefined}>
          {USER_STATUS_LABELS[row.status]}
        </span>
        <span>
          {row.passkeyCount} Passkey{row.passkeyCount === 1 ? '' : 's'}
        </span>

        {/* An outstanding invite is the state an admin most often needs to act
            on, so it says so rather than hiding behind "Eingeladen". */}
        {row.inviteExpiresAt && (
          <span className="text-warning">
            Einladung offen bis {new Date(row.inviteExpiresAt).toLocaleDateString('de-DE')}
          </span>
        )}

        {/* Only an active account has a password worth resetting: an invited
            one has not set one yet, and a disabled one is not meant to get in. */}
        {active && (
          <button
            type="button"
            disabled={busy}
            onClick={onResetPassword}
            className="hover:text-ink underline underline-offset-4 transition-colors disabled:opacity-40"
          >
            Passwort zurücksetzen
          </button>
        )}

        {/* For an active account this is the lost-authenticator path, and it
            must not read like a second way of doing the button beside it. */}
        <button
          type="button"
          disabled={busy}
          onClick={onReinvite}
          title={
            active
              ? 'Passwort und Authenticator neu einrichten — alle anderen Geräte werden abgemeldet'
              : undefined
          }
          className="hover:text-ink underline underline-offset-4 transition-colors disabled:opacity-40"
        >
          {active ? 'neu einrichten' : 'neu einladen'}
        </button>

        {!isSelf && (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={onToggle}
              className="hover:text-ink underline underline-offset-4 transition-colors disabled:opacity-40"
            >
              {row.status === 'disabled' ? 'aktivieren' : 'deaktivieren'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onDelete}
              className="hover:text-danger underline underline-offset-4 transition-colors disabled:opacity-40"
            >
              löschen
            </button>
          </>
        )}
      </span>
    </li>
  )
}
